import React, { useMemo } from 'react';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';
import { CollapsibleSection } from 'components/CollapsibleSection';
import {
  DATA_ROOM_NDA_SIGNING_STATUS,
  DataRoomNdaSigningStatus,
} from 'router/subrouters/DataRoom/models/DataRoomNDA';
import LockclosedIcon from 'icons/Lockclosed.icon';
import usePastTime from 'hooks/usePastTime';
import DataRoomNdaStatusBadge from '../../../DataRoomNdaStatusBadge';
import { dateFormat } from 'utils';
import { Button } from 'ncoded-component-library';
import BellIcon from 'icons/Bell.icon';
import useSendSignerReminder from '../../hooks';

import './DataRoomSignerNdaStatus.styles.scss';

const DATE_FORMAT = 'mmmm d, yyyy';

type DataRoomSignerNdaStatusProps = {
  className?: string;
  signingId: string;
  firstName: string;
  sentDate: Date;
  signedDate: Date;
  rejectionReason?: string;
  status: DataRoomNdaSigningStatus;
  onSendReminder: (signingId: string) => void;
};

const DataRoomSignerNdaStatus: React.FC<DataRoomSignerNdaStatusProps> = (
  props,
) => {
  const {
    className,
    status,
    signingId,
    sentDate,
    signedDate,
    rejectionReason,
    firstName,
    onSendReminder,
  } = props;

  const classes = classNames(
    'data-room-signer-nda-status',
    'white-panel',
    className,
  );

  const { t } = useTranslation();
  const pastTime = usePastTime();

  const { isLoading, sendReminder } = useSendSignerReminder(
    signingId,
    onSendReminder,
  );

  const TRIGGER: Record<DataRoomNdaSigningStatus, JSX.Element> = useMemo(
    () => ({
      [DATA_ROOM_NDA_SIGNING_STATUS.REJECTED]: (
        <div className="heading">
          <LockclosedIcon />
          {t('NDAstatus')}
          <DataRoomNdaStatusBadge
            status={status}
            rejectionReason={rejectionReason}
          />
        </div>
      ),
      [DATA_ROOM_NDA_SIGNING_STATUS.PENDING]: (
        <div className="heading">
          <LockclosedIcon />
          {t('NDAstatus')}
          <DataRoomNdaStatusBadge status={status} sentDate={sentDate} />
        </div>
      ),
      [DATA_ROOM_NDA_SIGNING_STATUS.NDA_EXECUTED]: (
        <div className="heading">
          <LockclosedIcon />
          {t('NDAstatus')}
          <DataRoomNdaStatusBadge status={status} signedDate={signedDate} />
        </div>
      ),
      [DATA_ROOM_NDA_SIGNING_STATUS.NOT_SENT]: (
        <div className="heading">
          <LockclosedIcon />
          {t('NDAstatus')}
          <DataRoomNdaStatusBadge status={status} />
        </div>
      ),
      [DATA_ROOM_NDA_SIGNING_STATUS.COUNTER_SIGNATURE_PENDING]: (
        <div className="heading">
          <LockclosedIcon />
          {t('NDAstatus')}
          <DataRoomNdaStatusBadge status={status} />
        </div>
      ),
    }),
    [sentDate, status, signedDate, rejectionReason, t],
  );

  const CONTENT: Record<DataRoomNdaSigningStatus, JSX.Element> = useMemo(
    () => ({
      [DATA_ROOM_NDA_SIGNING_STATUS.REJECTED]: (
        <div className="content">
          {t('userRejectedNDA', {
            name: firstName,
            rejectionReason: rejectionReason || '/',
          })}
        </div>
      ),
      [DATA_ROOM_NDA_SIGNING_STATUS.NDA_EXECUTED]: (
        <div className="content success">
          {signedDate
            ? t('NDAsignedWithDate', {
                firstName,
                date: dateFormat(signedDate, DATE_FORMAT),
              })
            : t('NDAsigned', { firstName })}
        </div>
      ),
      [DATA_ROOM_NDA_SIGNING_STATUS.PENDING]: (
        <div className="content">
          {t('NDAsent', {
            firstName,
            date: sentDate ? pastTime(sentDate) : null,
          })}

          <Button
            icon={BellIcon}
            variant="outline"
            disabled={isLoading}
            onClick={sendReminder}
          >
            {t('sendReminder')}
          </Button>
        </div>
      ),
      [DATA_ROOM_NDA_SIGNING_STATUS.NOT_SENT]: (
        <div className="content">{t('NDAnotSent')}</div>
      ),
      [DATA_ROOM_NDA_SIGNING_STATUS.COUNTER_SIGNATURE_PENDING]: (
        <div className="content">
          {t('NDAcounterSignaturePending', {
            firstName,
          })}
        </div>
      ),
    }),
    [
      firstName,
      rejectionReason,
      signedDate,
      sentDate,
      isLoading,
      t,
      pastTime,
      sendReminder,
    ],
  );

  return (
    <CollapsibleSection
      defaultOpen
      className={classes}
      trigger={TRIGGER[status]}
    >
      {CONTENT[status]}
    </CollapsibleSection>
  );
};

export default DataRoomSignerNdaStatus;
